import type { HttpError } from "@bankmonitor/bm-ui-kit";
import { BmUIKitConfig, Http } from "@bankmonitor/bm-ui-kit";
import type React from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, useHref, useLinkClickHandler } from "react-router-dom";
import AuthenticationService from "@service/AuthenticationService";
import { getBaseUrl } from "@util/Config";
import { initGtm } from "@util/measurement/gtm";
import { ReadableError } from "@util/ReadableError";
import { Root } from "./Root";
import type { TenantConfig } from "./tenant/tenant.config";

declare global {
	interface Window {
		BM_NEED_RELOAD: boolean;
		TENANT: TenantConfig;
	}
}

// TOKEN
Http.setPostProcess((url: string, _init: RequestInit, cb: () => Promise<any>) =>
	cb()
		.catch((error: HttpError) => {
			if (error.status === 403 && error.message?.toString().includes("CSRF")) {
				return AuthenticationService.init().then(() =>
					cb().catch((error2) => {
						if (error2.status === 403 && error2.message?.toString().includes("CSRF")) {
							throw new ReadableError("Munkamenet lejárt!", {
								error: error2,
								needLog: false,
							});
						}

						throw error2;
					}),
				);
			}

			throw error;
		})
		.then((response) => {
			if ((url.startsWith("/") || url.startsWith(getBaseUrl())) && !url.includes("VERSION")) {
				AuthenticationService.updateSession();
			}

			return response;
		}),
);

// UI kit link element
BmUIKitConfig.setOptions({
	customLinkProvider: (props) => {
		if (window.BM_NEED_RELOAD || !props.href || !props.href.startsWith("/") || props.download) {
			return props;
		}

		const href = useHref(props.href);
		const internalOnClick = useLinkClickHandler(props.href, {
			target: props.target,
		});

		function handleClick(event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) {
			if (props.onClick) {
				props.onClick(event);
			}

			if (!event.defaultPrevented) {
				internalOnClick(event);
			}
		}

		return {
			href,
			target: props.target,
			onClick: handleClick,
		};
	},
});

initGtm();

async function init() {
	const rootElement = document.getElementById("root");
	const root = createRoot(rootElement);
	root.render(
		<BrowserRouter>
			<Root />
		</BrowserRouter>,
	);
}
init();
